import { prepareCanvas } from "../lib/canvas";
import { mixHexColors } from "../lib/colors";
import { ease } from "../lib/ease";
const hoverDuration = 300;

function getTimelineValue(timelineValA, timeA, timeB, duration, direction = 1) {
  const deltaPerc = (timeB - timeA) / duration;
  return Math.min(1, Math.max(0, timelineValA + deltaPerc * direction));
}

function pointerWithDpi(pointer, dpi = 1) {
  return {
    x: pointer.x * dpi,
    y: pointer.y * dpi
  };
}

function isInteracting(pointer, width, height, donutWidth) {
  const centerX = width / 2;
  const centerY = height / 2;
  const radius = Math.min(width, height) / 2;
  const innerRadius = radius * donutWidth;
  const distance = Math.sqrt((pointer.x - centerX) ** 2 + (pointer.y - centerY) ** 2);
  return distance >= innerRadius && distance <= radius;
}

export async function pieChart(canvas, {
  sets
} = {}, {
  donutWidth = 0.64,
  chartContent,
  onHover
} = {}) {
  let lastRenderHash = null;
  let hovered = null;
  const $sectionStates = new WeakMap();

  const getState = (section, then) => {
    if (!section) {
      return;
    }

    let state = $sectionStates.get(section);

    if (!state) {
      state = {
        active: true,
        timeline: {
          direction: 1,
          val: 1,
          duration: hoverDuration,
          active: false,
          timestamp: null
        }
      };
      $sectionStates.set(section, state);
    }

    then?.(state);
    return state;
  };

  function render(...args) {
    const hash = JSON.stringify(args); // console.log("render pie", args[0]);

    if (hash === lastRenderHash) {
      return;
    }

    let {
      pointer,
      sets,
      rect,
      dpi,
      userAction = false
    } = args[0];
    dpi = Math.max(1, dpi);
    pointer = pointer ? pointerWithDpi(pointer, dpi) : null;
    canvas.width = rect.width * dpi;
    canvas.height = rect.width * dpi;

    if (!canvas.width || !canvas.height) {
      return;
    }

    const {
      width,
      height
    } = canvas;
    const centerX = width / 2;
    const centerY = height / 2;
    const {
      ctx
    } = prepareCanvas(canvas);
    sets = sets.filter(set => set.value > 0);
    let total = sets.reduce((acc, set) => acc + set.value, 0);
    let startAngle = -Math.PI / 2;
    let endAngle = 0;
    const oldHovered = hovered;
    let newHovered = null;
    const radius = Math.min(width, height) / 2;
    let isWithin = pointer ? Math.sqrt((pointer.x - centerX) ** 2 + (pointer.y - centerY) ** 2) <= radius : false;

    for (let set of sets) {
      const percentValue = set.value / total;
      endAngle = startAngle + 2 * Math.PI * percentValue;
      const innerRadius = radius * donutWidth; // Outer arc

      ctx.beginPath();
      ctx.arc(centerX, centerY, radius - Math.round(ctx.lineWidth / 2), startAngle, endAngle); // ctx.strokeStyle = 'black';

      ctx.lineWidth = 1; // Line from outer arc to inner arc

      ctx.lineTo(centerX + innerRadius * Math.cos(endAngle), centerY + innerRadius * Math.sin(endAngle)); // Inner arc

      ctx.arc(centerX, centerY, innerRadius, endAngle, startAngle, true); // ctx.closePath();
      // Fill the segment

      if (pointer && isInteracting(pointer, width, height, donutWidth)) {
        if (ctx.isPointInPath(pointer.x, pointer.y) && !newHovered) {
          newHovered = set;
        }
      }

      const state = getState(set);
      const fillColor = mixHexColors("#77777709", set.color, ease.easeInOutCubic(state.timeline.val));
      const strokeColor = mixHexColors("#77777733", set.color, ease.easeInOutCubic(state.timeline.val));
      ctx.fillStyle = fillColor;
      ctx.strokeStyle = strokeColor;
      ctx.fill();
      ctx.stroke();
      startAngle = endAngle;
    }

    if (!userAction) {
      return;
    }

    if (newHovered) {
      // canvas.style.cursor = "pointer";
      if (newHovered !== oldHovered) {
        ctrl.setActive(newHovered, true);
      }
    } else {
      // canvas.style.cursor = "default";
      if (pointer && !isWithin && oldHovered) {
        ctrl.blur();
      }
    }

    hovered = newHovered;
  }

  canvas.addEventListener("mousemove", e => {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const dpi = window.devicePixelRatio || 1;
    render({
      pointer: {
        x,
        y
      },
      sets,
      rect,
      dpi,
      userAction: true
    });
  }, true);
  chartContent.addEventListener("mouseleave", () => {
    ctrl.blur();
  }, true);

  function animate() {
    const now = Date.now();
    let isAnimating = false;
    sets.forEach(set => {
      getState(set, state => {
        if (state.timeline.active) {
          state.timeline.val = getTimelineValue(state.timeline.val, state.timeline.timestamp, now, state.timeline.duration, state.timeline.direction);
          state.timeline.timestamp = now;

          if (state.timeline.direction === -1 && state.timeline.val === 0 || state.timeline.direction === 1 && state.timeline.val === 1) {
            state.timeline.active = false;
          } else {
            isAnimating = true;
          }
        }
      });
    });
    render({
      sets,
      rect: canvas.getBoundingClientRect(),
      dpi: window.devicePixelRatio || 1
    });

    if (isAnimating) {
      requestAnimationFrame(animate);
    }
  }

  const update = () => {
    render({
      sets,
      rect: canvas.getBoundingClientRect(),
      dpi: window.devicePixelRatio || 1
    });
  };

  let interval = setInterval(() => {
    if (!canvas.parentNode) {
      clearInterval(interval);
      return;
    }

    update();
  }, 1000);
  update();
  const ctrl = {
    canvas,
    update,
    destroy: () => {
      clearInterval(interval);
    },

    setActive(activeSet, direct = false, active = true) {
      sets.forEach(set => {
        getState(set, state => {
          if (activeSet === set) {
            state.active = true;
            state.timeline.direction = 1;
          } else {
            state.active = false;
            state.timeline.direction = -1;
          }

          state.timeline.active = true;
          state.timeline.timestamp = Date.now();
        });
      });
      onHover?.(activeSet, hovered, direct);
      hovered = activeSet;
      animate();
    },

    blur() {
      sets.forEach(set => {
        getState(set, state => {
          state.active = true;
          state.timeline.direction = 1;
          state.timeline.active = true;
          state.timeline.timestamp = Date.now();
        });
      });
      onHover?.(null, hovered);
      hovered = null;
      animate();
    }

  };
  return ctrl;
}